/**
 *
 * Input Upload element
 *
 */
import * as React from 'react';
import { HTMLAttributes, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Box,
  BoxProps,
  Fab,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { LoadingIndicator } from 'app/components/LoadingIndicator';
import { useSnackbar } from 'notistack';

interface Props extends Omit<Partial<HTMLAttributes<HTMLDivElement>>, 'title'> {
  loading: boolean;
  progress: number;
  onFileChange: (file?: File) => void;
  logo_url?: string;
  title?: ReactNode;
  imageBoxProps?: Pick<BoxProps, 'sx'>;
  maxFileSize?: number;
}

export function ImageUploadFormElement(props: Props) {
  const {
    loading,
    progress,
    onFileChange,
    logo_url,
    title = 'Upload image',
    imageBoxProps = {},
    maxFileSize = 2 * 1024 * 1024,
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const [stage, setStage]: [stage: number, setStage: Function] = React.useState(
    logo_url ? 3 : 1,
  );
  const [imageSrc, setImageSrc] = useState<string | null>(logo_url || null);

  const [file, setFile]: [file: File | undefined, setFile: Function] =
    React.useState(undefined);

  const removeFile = () => {
    setStage(1);
    setImageSrc(null);
    setFile(undefined);
  };

  const fileChange = e => {
    const [f] = e.target.files || e.dataTransfer.files;
    if (f) {
      console.log(f.size);
      if (f.size > maxFileSize) {
        const fileSizeInMB = (maxFileSize / (1024 * 1024)).toFixed(1);
        enqueueSnackbar(`Image must be smaller than ${fileSizeInMB}MB`, {
          variant: 'error',
        });
        removeFile();
        e.target.value = null;
        return;
      }
      setFile(f);
      setStage(3);
    }
  };

  // reset to the idle stage once an upload finishes; keyed to loading only
  useEffect(() => {
    if (stage === 4 && !loading) {
      setStage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // generate the preview and notify the parent when the local file/imageSrc
  // changes; onFileChange is an unstable prop callback, not a trigger
  useEffect(() => {
    try {
      if (file) {
        const reader = new FileReader();
        reader.addEventListener('load', function () {
          setImageSrc(reader.result as string);
        });
        reader.readAsDataURL(file);

        if (!loading) {
          onFileChange(file);
        }
      } else if (!imageSrc && imageSrc !== logo_url) {
        // remove if image src is empty and original prop was not
        onFileChange();
      }
    } catch (e) {
      console.log(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc, file]);

  const content = useMemo(() => {
    switch (stage) {
      case 1:
      case 2:
        return (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              cursor: 'pointer',
              paddingTop: 4,
              paddingBottom: 4,
            }}
          >
            <Fab color="primary" size={'small'}>
              <Add />
            </Fab>
            <Box
              sx={{
                ml: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h5" color="primary">
                {title}
              </Typography>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ textDecoration: 'underline' }}
              >
                Select from folder
              </Typography>
            </Box>

            <input
              disabled={loading}
              type="file"
              accept={['image/jpeg', 'image/png'].join(', ')}
              onChange={fileChange}
              style={{
                opacity: 0,
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                cursor: 'pointer',
              }}
            />
          </Box>
        );

      case 3:
        return (
          <Paper
            elevation={2}
            sx={{
              width: 'auto',
              display: 'inline-flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: 600,
              p: 1,
              pt: 4,
              position: 'relative',
              backgroundColor: 'grey.A100',
            }}
          >
            <IconButton
              disabled={loading}
              size="small"
              color="error"
              onClick={() => removeFile()}
              sx={{ position: 'absolute', right: 0, top: 0 }}
            >
              <Close />
            </IconButton>

            {!!imageSrc ? (
              <Box
                sx={{
                  backgroundRepeat: 'no-repeat',
                  backgroundImage: `url("${imageSrc}")`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center center',
                  width: 200,
                  height: 200,
                  ...(imageBoxProps.sx || {}),
                }}
              ></Box>
            ) : (
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  ...(imageBoxProps.sx || {}),
                }}
              ></Box>
            )}
          </Paper>
        );

      case 4:
        return (
          <LoadingIndicator
            minHeight={300}
            ProgressProps={{
              variant: 'determinate',
              value: progress * 100,
            }}
            message={
              <>
                <Typography variant="h5">
                  Uploading - {(progress || 0).toFixed(0)}%
                </Typography>
              </>
            }
          />
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, file]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      {content}
    </Box>
  );
}
